'use client'

import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { useEffect } from 'react'
import {
    isConsentGateConfigured,
    whenTrackingAllowed,
} from '../consent/consentTrackingStore'

const APPLICATION_ID = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || ''
const CLIENT_TOKEN = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || ''
const SERVICE = process.env.NEXT_PUBLIC_DATADOG_SERVICE || ''
const VERCEL_BRANCH = process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL
const VERSION = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || ''
let ENVIRONMENT = process.env.NEXT_PUBLIC_VERCEL_ENV || 'development'

if (ENVIRONMENT === 'preview' && VERCEL_BRANCH) {
    ENVIRONMENT = VERCEL_BRANCH.split('-git-')[1].split('.vercel.app')[0]
}

// init() writes the `_dd_s` session cookie and starts beaconing, so it waits
// for the storefront's consent gate. Nothing is collected before it runs:
// console output, runtime errors and failed requests from before init are
// dropped, not delayed. Explicit API calls made earlier are buffered by the
// SDK and replayed on init.
const initDatadog = () => {
    if (datadogRum.getInitConfiguration()) return

    datadogRum.init({
        clientToken: CLIENT_TOKEN,
        applicationId: APPLICATION_ID,
        site: 'datadoghq.com',
        service: SERVICE,
        env: ENVIRONMENT,
        version: VERSION,
        sessionSampleRate: 10,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
    })

    datadogLogs.init({
        clientToken: CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: SERVICE,
        env: ENVIRONMENT,
        version: VERSION,
        forwardConsoleLogs: 'all',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
    })
}

type DatadogInitProps = {
    landingPageHash: string | undefined
}

export default function DatadogInit({ landingPageHash }: DatadogInitProps) {
    useEffect(() => {
        // LandingPage configures the gate in a layout effect, which runs
        // before this one. A document that never does (crowlink redirects,
        // index, the desktop preview shell around the phone iframe) has no
        // modal that could open it, so Datadog starts right away.
        if (!isConsentGateConfigured()) {
            initDatadog()
            return
        }
        return whenTrackingAllowed(initDatadog)
    }, [])

    useEffect(() => {
        if (landingPageHash) {
            datadogRum.setGlobalContextProperty(
                'landing_page_hash',
                landingPageHash,
            )
        }
        return () => datadogRum.removeGlobalContextProperty('landing_page_hash')
    }, [landingPageHash])

    return null
}
